import React, { useEffect, useId, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { PRACTICE_FACTS } from '@/lib/practiceStats';
import { CONSULTATIONS, SIGNATURE_BOOKING_PATH, VIRTUAL_BOOKING_PATH } from '@/lib/consultationBooking';
import './PatientQuiz.css';

const TREATMENTS = [
  { id: 'implants', label: 'Dental Implants', detail: 'Replace missing teeth' },
  { id: 'cosmetic', label: 'Cosmetic Dentistry', detail: 'Veneers, whitening & smile makeovers' },
  { id: 'invisalign', label: 'Invisalign / Aligners', detail: 'Straighten your smile' },
  { id: 'general', label: 'General Dentistry', detail: 'Explore your dental care options' },
  { id: 'unsure', label: "I'm not sure yet", detail: '' },
];

export default function PatientQuiz({ initialTreatment = null }) {
  const initial = TREATMENTS.find(item => item.id === initialTreatment) || null;
  const [treatment, setTreatment] = useState(initial);
  const [choosingConsultation, setChoosingConsultation] = useState(Boolean(initial));
  const headingRef = useRef(null);
  const didInteract = useRef(false);
  const id = useId();
  const location = useLocation();

  // Preserve an explicit landing-page selection without storing treatment answers.
  useEffect(() => {
    const selected = TREATMENTS.find(item => item.id === initialTreatment) || null;
    setTreatment(selected);
    setChoosingConsultation(Boolean(selected));
    didInteract.current = false;
  }, [location.pathname, initialTreatment]);

  useEffect(() => {
    if (didInteract.current) headingRef.current?.focus({ preventScroll: true });
  }, [choosingConsultation]);

  const chooseTreatment = (item) => {
    didInteract.current = true;
    setTreatment(item);
    setChoosingConsultation(true);
  };

  return (
    <section className="tda-consult-quiz" data-consultation-quiz aria-labelledby={id + '-heading'}>
      <div className="tda-consult-quiz__body">
        <div className="tda-consult-quiz__progress">
          <span>Question {choosingConsultation ? '2' : '1'} of 2</span>
          <span aria-hidden="true" className="tda-consult-quiz__dots">
            <i className="is-active" /><i className={choosingConsultation ? 'is-active' : ''} />
          </span>
        </div>
        <h2 ref={headingRef} id={id + '-heading'} tabIndex={-1}>
          {choosingConsultation ? 'How would you like to get started?' : 'What treatment are you interested in?'}
        </h2>
        {!choosingConsultation ? (
          <>
            <p className="tda-consult-quiz__intro">Choose the option that fits you best.</p>
            <div className="tda-consult-quiz__treatments">
              {TREATMENTS.map(item => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => chooseTreatment(item)}
                  className="tda-consult-quiz__treatment"
                  aria-label={item.label}
                >
                  <strong>{item.label}</strong>
                  {item.detail && <small>{item.detail}</small>}
                  <span aria-hidden="true">→</span>
                </button>
              ))}
            </div>
          </>
        ) : (
          <>
            <div className="tda-consult-quiz__selection">
              <span>Interested in: <strong>{treatment?.label}</strong></span>
              <button type="button" onClick={() => { didInteract.current = true; setChoosingConsultation(false); }}>
                Change
              </button>
            </div>
            <div className="tda-consult-quiz__plans">
              {CONSULTATIONS.map(consult => (
                <article key={consult.id} className={'tda-consult-quiz__plan ' + (consult.id === 'signature' ? 'is-signature' : '')}>
                  <span className="tda-consult-quiz__type">{consult.location}</span>
                  <h3>{consult.label}</h3>
                  <div className="tda-consult-quiz__price">
                    ${consult.price}
                  </div>
                  <p>{consult.description}</p>
                  <ul>{consult.inclusions.map(item => <li key={item}>{item}</li>)}</ul>
                  <div className="tda-consult-quiz__action">
                    <a
                      href={consult.id === 'signature' ? SIGNATURE_BOOKING_PATH : VIRTUAL_BOOKING_PATH}
                      className={'tda-consult-quiz__book ' + (consult.id === 'virtual' ? 'is-secondary' : '')}
                      aria-describedby={id + '-' + consult.id + '-instructions'}
                      data-consultation-booking={consult.id}
                    >
                      Book Signature Consult <span aria-hidden="true">↗</span>
                    </a>
                    <p id={id + '-' + consult.id + '-instructions'} className="tda-consult-quiz__instructions">
                      Choose Signature Consult in the online scheduler. Our team will confirm the appointment and arrange prepayment.
                    </p>
                  </div>
                </article>
              ))}
            </div>
          </>
        )}
        <p className="tda-consult-quiz__help">
          Prefer to speak with us? <a href={PRACTICE_FACTS.phoneHref}>Call {PRACTICE_FACTS.phone}</a>
        </p>
      </div>
      <div className="tda-consult-quiz__footer">
        Dental emergency or existing appointment? <a href={PRACTICE_FACTS.phoneHref}>Call {PRACTICE_FACTS.phone}</a>.
      </div>
    </section>
  );
}